import { useEffect, useContext, useRef } from 'react'
import { AppRedirect } from 'lib/routing/AppRedirect'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { fetchTourV2Offer } from 'actions/tour/TourV2Actions'
import fetchTourBundle from 'actions/tour/fetchTourBundle'
import { getTourV2CartItems } from 'selectors/TourV2/tourV2OfferPageSelectors'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import TourV2Content from 'components/OfferPage/TourV2/TourV2Content'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import { offerViewed } from 'actions/OfferActions'
import { tourV2OfferProductDetailView } from 'actions/AnalyticsActions'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import GeoContext from 'contexts/geoContext'

import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { tourOfferClicked } from 'analytics/eventDefinitions'
import useQueryParams from 'hooks/useQueryParams'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { min } from 'lib/array/arrayUtils'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

interface MappedStateProps {
  checkoutItems: Array<App.Checkout.TourV2Item>
  isOfferLoading?: boolean
  offer?: App.Tours.TourV2Offer
  offerError?: any
  currency: string
  isPrivateRequest: boolean
}

interface OwnProps {
  offerId: string
  privateRequestKey?: string
  variationId: string
}

function TourV2OfferVariationPage(props: OwnProps & MappedStateProps) {
  const {
    checkoutItems,
    isOfferLoading,
    isPrivateRequest,
    offerId,
    offer,
    offerError,
    currency,
    variationId,
  } = props
  const geo = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const trackedOfferKeyRef = useRef<string | undefined>(undefined)
  const initialisedOfferKeyRef = useRef<string | undefined>(undefined)

  useEuropeOfferPageWalledGarden()

  const [offerPageState, offerPageDispatch] = useOfferPageReducer({
    ...INITIAL_OFFER_PAGE_STATE,
    tourV2Offer: offer,
    tourV2Variation: offer?.variations ? offer.variations[variationId] : undefined,
    displayMemberPrice: !!offer?.luxPlus.hasMemberPrices,
  })
  const queryParams = useQueryParams()

  useEffect(() => {
    if (!offer) return
    const trackingKey = `${offer.id}-${currency}`
    if (trackingKey === trackedOfferKeyRef.current) return
    trackedOfferKeyRef.current = trackingKey
    fireEcommerceGTMEvent(tourOfferClicked(offer, currency))
  }, [currency, offer])

  usePageActivityTracking()

  useEffect(() => {
    if (offer) {
      dispatch(fetchTourBundle(offer.id))
    }
  }, [dispatch, offer])

  useEffect(() => {
    // Only attempt to fetch if we don't have the offer, not currently loading, and no error
    if (!offer && !isOfferLoading && !offerError) {
      const privateRequestKey = queryParams.get('privateRequestKey') ?? undefined
      dispatch(fetchTourV2Offer(offerId, privateRequestKey))
    }
  }, [dispatch, offerId, queryParams, offer, isOfferLoading, offerError])

  useEffect(() => {
    if (offer) {
      const variation = offer.variations[variationId] ?? offer.variations[Object.keys(offer.variations)[0]]
      offerPageDispatch({
        type: OfferPageActions.setTourV2Offer,
        variation,
        offer,
      })

      const offerKey = `${offer.id}-${props.privateRequestKey ?? ''}`
      if (initialisedOfferKeyRef.current === offerKey) return
      initialisedOfferKeyRef.current = offerKey

      if (offer.luxPlus.hasMemberPrices) {
        dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
      }

      const availableDepartures = variation.departures.filter(departure => departure.options.length > 0)
      const earliestDate = min(variation.departures.map(departure => new Date(departure.startDate)))
      const earliestAvailableDate = min(availableDepartures.map(departure => new Date(departure.startDate)))

      if (earliestAvailableDate && earliestDate !== earliestAvailableDate) {
        offerPageDispatch({
          type: OfferPageActions.setDepartureMonth,
          month: { month: earliestAvailableDate.getMonth(), year: earliestAvailableDate.getFullYear() },
        })
      }

      if (checkoutItems?.length) {
        const offerCheckoutItems = checkoutItems?.filter((item) => item.offerId === offer.id) ?? []

        const roomOccupants = offerCheckoutItems.map(item => generateOfferPageRoom(item.occupancy))

        if (roomOccupants.length) {
          offerPageDispatch({
            type: OfferPageActions.setRooms,
            rooms: roomOccupants,
          })
        }
      }

      dispatch(tourV2OfferProductDetailView(offer))

      if (ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type) && !isPrivateRequest) {
        dispatch(offerViewed(offer.id, offer.type))
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, isPrivateRequest])
  if (offerError) {
    if (offerError.status === 404) {
      return <Error404PageWithOffers />
    } else {
      return <Error500Page error={offerError} />
    }
  }

  if (isOfferLoading || !offer || !offerPageState.tourV2Offer) {
    return <LoadingIndicator />
  }

  if (offer) {
    // if URL use the tour option id then redirect to the url using the tour id (slug)
    if (!offer.variations[variationId]) {
      const matchedVariationByTourOptionId = Object.values(offer.variations).find(variation => variation.fkTourOptionId === variationId)

      if (matchedVariationByTourOptionId) {
        const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${offer.id}/${matchedVariationByTourOptionId.id}`
        return <RedirectWithStatus code={301} to={toUrl} />
      }

      // if slug variation not found we redirect to default tour offer page
      const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${offer.id}`
      return <AppRedirect to={toUrl} />
    }
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageStateProvider state={offerPageState} dispatch={offerPageDispatch}>
      <OfferPageWrapper className="OfferPage">
        {!!offerPageState.tourV2Offer && <TourV2Content
          key={offer.id}
        />}
      </OfferPageWrapper>
    </OfferPageStateProvider>
  </AnalyticsPageContext.Provider>
}

const ConnectedTourV2OfferVariationPage = connect((state: App.State, ownProps: OwnProps): MappedStateProps => {
  const { offerId, privateRequestKey } = ownProps
  const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

  const offer = state.offer.tourV2Offers[offerKey]

  return {
    checkoutItems: getTourV2CartItems(state),
    isOfferLoading: state.offer.offersLoading[offerKey],
    offer,
    offerError: state.offer.offerErrors[offerKey],
    currency: state.geo.currentCurrency,
    isPrivateRequest: !!privateRequestKey,
  }
})(TourV2OfferVariationPage)

function TourV2OfferVariationPageRoute() {
  const { offerId = '', variationId = '' } = useParams<'offerId' | 'variationId'>()
  const privateRequestKey = useQueryParams().get('privateRequestKey') ?? undefined

  return <ConnectedTourV2OfferVariationPage offerId={offerId} variationId={variationId} privateRequestKey={privateRequestKey} />
}

export default TourV2OfferVariationPageRoute
